import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiError, uploadFiles } from "@/lib/api";
import { SignedImg } from "@/components/shared/SignedImg";
import { PhotoLightbox } from "@/components/shared/PhotoLightbox";
import { compressImage } from "@/lib/image";
import { useAuth, isManager } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { captureGps, attendanceError } from "@/lib/gps";
import { LogIn, LogOut, ListChecks, CalendarClock, Check, X, Package, History, Pencil, ArrowLeftRight } from "lucide-react";
import { usePhotoInputs, PhotoUploadButton } from "@/components/shared/PhotoPicker";
import { Sec, BreakChips } from "@/components/shared/ShiftSections";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatTime, formatCZK, formatDate } from "@/lib/format";
import TaskDetailModal from "@/components/TaskDetailModal";
import DayCloseModal from "@/components/DayCloseModal";
import SitePicker from "@/components/mobile/SitePicker";
import { storage } from "@/lib/storage";
import HoursChart from "@/components/HoursChart";
import { SwipeRow } from "@/components/tasks/TaskRows";
import { useTaskRowActions } from "@/components/tasks/useTaskRowActions";

function useTick(active) {
  const [, setNow] = useState(Date.now());
  useEffect(() => { if (!active) return; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [active]);
}
function elapsed(fromIso) {
  const diff = Math.max(0, Date.now() - new Date(fromIso).getTime());
  const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}
function elapsedHours(fromIso) {
  return Math.max(0, Date.now() - new Date(fromIso).getTime()) / 3600000;
}
function hmm(fromIso) {
  const diff = Math.max(0, Date.now() - new Date(fromIso).getTime());
  const h = Math.floor(diff / 3600000), m = Math.floor((diff % 3600000) / 60000);
  return `${h}:${String(m).padStart(2, "0")}`;
}

// Telefon (198): oznámení „ostrůvek“ — kde, od kdy / kolik zapsáno. Počítač beze změny.
const isPhone = () => { try { return window.matchMedia("not all and (min-width: 768px)").matches; } catch { return false; } };   // = mobilní lišty (md:hidden)
const hmNow = () => formatTime(new Date().toISOString()).replace(/^0(\d)/, "$1");
const PHONE_NO_GPS = { description: "Zapni polohu — jinak docházku musí ručně schválit vedoucí" };

function fmtHours(h) {
  return `${(h || 0).toFixed(1).replace(".", ",")} h`;
}

// Sekce dialogu — kopie z InvestorEditDialog.js (záměrně nesjednoceno do shared/).
const MAX_PHOTOS = 10;
// Střídavá hláška po uzavření dne — ať to není každý den stejná věta.
const CLOSE_MSGS = ["Dnes hotovo", "Dobrá práce", "Den zapsán, díky", "Docházka uzavřena", "Hotovo, zapsáno"];

// Mezikrok „Moje směna“ (196) — po podržení tlačítka docházky na telefonu: jinou
// stavbu, nebo odchod. Vlastní tikání po sekundách, ať se nepřekresluje celý blok.
function ShiftStep({ open, shift, onOpenChange, onSwitch, onLeave }) {
  const [, setNow] = useState(0);
  useEffect(() => { if (!open) return; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [open]);
  const start = shift?.check_in;
  const diff = start ? Math.max(0, Date.now() - new Date(start).getTime()) : 0;
  const sec = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent sheet className="max-w-md text-center" data-testid="shift-step-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogTitle className="sr-only">Moje směna</DialogTitle>
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-xl bg-success/15 text-success px-2.5 py-1 text-[13px] font-bold">
            <span className="w-[7px] h-[7px] rounded-full bg-success animate-pulse motion-reduce:animate-none" />V práci od {start ? formatTime(start).replace(/^0(\d)/, "$1") : "—"}
          </span>
          <div className="mt-2.5 whitespace-nowrap tabular-nums font-heading" data-testid="shift-step-elapsed">
            <span className="text-[56px] leading-none font-extrabold">{start ? hmm(start) : "0:00"}</span>
            <span className="text-2xl font-bold text-muted-foreground">:{sec}</span>
          </div>
          <div className="mt-1.5 text-base font-semibold truncate">{shift?.project_name || "—"}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <button type="button" onClick={onSwitch} data-testid="shift-step-switch"
            className="h-[52px] rounded-full bg-secondary text-[15px] font-bold inline-flex items-center justify-center gap-2 min-w-0 whitespace-nowrap active:scale-[.97] transition-transform motion-reduce:transition-none">
            <ArrowLeftRight size={18} aria-hidden="true" />Jiná stavba
          </button>
          <button type="button" onClick={onLeave} data-testid="shift-step-leave"
            className="h-[52px] rounded-full bg-foreground text-background text-[15px] font-bold inline-flex items-center justify-center gap-2 min-w-0 whitespace-nowrap active:scale-[.97] transition-transform motion-reduce:transition-none">
            <LogOut size={18} aria-hidden="true" />Odchod z práce
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// headless (196): jen dialogy a obsluha tlačítka docházky (/?shift=1) — vzhled
// karty si kreslí mobilní přehled vedení; onState mu posílá načtená data.
// hideCard (199): bez karty „Moje směna“ — úvod dělníka na telefonu si ji kreslí sám,
// úkoly, hodiny i dialogy zůstávají.
export default function MyShiftBlock({ mode = "site", headless = false, hideCard = false, onState }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [ov, setOv] = useState(null);
  const [loaded, setLoaded] = useState(false);   // true až když jsou VŠECHNA data pro rozhodnutí o stavu směny
  const [pd, setPd] = useState(null);
  const [day, setDay] = useState(null);
  const [breakRule, setBreakRule] = useState(null);
  const [todayHours, setTodayHours] = useState(0);   // součet dnešních odeslaných výkazů
  const [dayPhotosReq, setDayPhotosReq] = useState(false);   // povinné fotky v režimu výkazu (161)
  const [closeOpen, setCloseOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [stepOpen, setStepOpen] = useState(false);
  const [report, setReport] = useState("");
  const [busy, setBusy] = useState(false);
  // 215: druhé podržení / klepnutí během čekání na polohu nesmí poslat druhý příchod (krátká směna navíc)
  const running = useRef(false);
  // Když hledání polohy trvá, ať člověk vidí, že se něco děje (dřív až 20 s ticha).
  const locate = async (opts) => {
    let id = null;
    const t = setTimeout(() => { id = toast.loading("Zjišťuji polohu…"); }, 600);
    try { return await captureGps(opts); } finally { clearTimeout(t); if (id != null) toast.dismiss(id); }
  };
  // rich checkout state
  const [siteTasks, setSiteTasks] = useState([]);
  const [doneIds, setDoneIds] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [breakMin, setBreakMin] = useState("");
  const [stockItems, setStockItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [upProg, setUpProg] = useState(null);   // 257: „Nahrávám 3 z 10“
  const [taskModalId, setTaskModalId] = useState(null);
  const [lightbox, setLightbox] = useState(null);   // URL fotky v celoplošném náhledu
  const photoPick = usePhotoInputs({ onChange: (e) => addPhotos(e), disabled: uploading });
  const shift = ov?.shift;

  const load = useCallback(async () => {
    const [ovRes, pdRes, dayRes] = await Promise.all([
      api.get("/me/overview").catch(() => null),
      api.get("/dashboard/personal").catch(() => null),
      mode === "daysheet" ? api.get("/daysheet/current").catch(() => null) : Promise.resolve(null),
    ]);
    if (ovRes) setOv(ovRes.data);
    if (pdRes) setPd(pdRes.data);
    if (mode === "daysheet") { setDay(dayRes?.data?.day || null); setBreakRule(dayRes?.data?.breakRule || null); setTodayHours(dayRes?.data?.todayHours || 0); setDayPhotosReq(!!dayRes?.data?.photos_required); }
    setLoaded(true);
  }, [mode]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { onState?.({ loaded, ov, pd, day, todayHours, mode }); }, [loaded, ov, pd, day, todayHours, mode, onState]);
  // Telefon odemčený ráno po noci: načíst znovu, ať karta neukazuje včerejšek.
  useEffect(() => {
    if (!headless) return undefined;
    const vis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", vis);
    return () => document.removeEventListener("visibilitychange", vis);
  }, [headless, load]);

  // Úkoly na dnešek jdou na Přehledu (úzké rozhraní) odbavit stejnými tahy
  // jako v Úkolech — sdílená obsluha řádku (zadání 98). Na Přehledu jsou jen
  // moje úkoly, takže bez optimistického patchLocal; po akci se volá load().
  const todayNavIds = (ov ? [...(ov.tasks_overdue || []), ...(ov.tasks_today || [])] : []).map((t) => t.id);
  const rowActions = useTaskRowActions({ reload: load, navIds: todayNavIds });

  const isDaysheet = mode === "daysheet";
  const dayOpen = isDaysheet && day && day.status === "open";
  const dayDraft = isDaysheet && day && (day.status === "draft" || day.status === "returned");
  const daySubmitted = isDaysheet && day && day.status === "submitted";
  const runningStart = shift ? shift.check_in : (dayOpen ? day.started_at : null);
  // Okno pro opravu odeslaného výkazu: 15 minut od odeslání, pak už jen vedoucí.
  const canEditSubmitted = daySubmitted && day?.submitted_at
    && (Date.now() - new Date(day.submitted_at).getTime()) < 15 * 60 * 1000;
  // Tikat i po odeslání, ať tlačítko „Upravit výkaz" po vypršení okna samo zmizí.
  useTick(!!runningStart || !!daySubmitted || checkoutOpen);

  // Náhled zapsaných hodin — zrcadlí services.py:segment_hours (měnit obojí spolu).
  const gross = shift ? elapsedHours(shift.check_in) : 0;
  const brkH = breakMin === "" ? (gross > 6 ? 0.5 : 0)
    : Math.max(0, Math.min(720, Number(breakMin) || 0)) / 60;
  const netHours = Math.max(gross - brkH, 0);
  const netText = netHours <= 0
    ? "Přestávka je delší než směna — zapsalo by se 0 h."
    : brkH === 0
    ? `Zapíše se ${fmtHours(netHours)}.`
    : breakMin === ""
    ? `Zapíše se ${fmtHours(netHours)} · ${fmtHours(gross)} minus zákonných 30 min.`
    : `Zapíše se ${fmtHours(netHours)} · ${fmtHours(gross)} minus přestávka ${Number(breakMin)} min.`;

  // Deep-link from mobile FAB: /?shift=1 opens the right shift action.
  useEffect(() => {
    if (!loaded || !ov) return;
    const params = new URLSearchParams(location.search);
    if (params.get("shift") !== "1") return;
    if (isDaysheet) {
      if (dayOpen) endDay();
      else if (dayDraft) setCloseOpen(true);
      // Odeslaný výkaz směnu uzavírá — FAB rovnou přihlásí další směnu dne.
      else if (daySubmitted) startDay();
      else startDay();
    } else if (ov.shift) {
      // telefon: nejdřív mezikrok (jiná stavba / odchod), počítač beze změny;
      // move=1 (rychlá akce Jiná stavba) rovnou výběr stavby
      if (params.get("move") === "1") setPickOpen(true);
      else isPhone() ? setStepOpen(true) : openCheckout();
    } else if (params.get("site") && (ov.my_projects || []).some((p) => p.id === params.get("site"))) {
      doCheckIn(params.get("site"));   // 199: předvolená stavba z karty směny
    } else {
      setPickOpen(true);
    }
    nav(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, ov, day, location.search]);

  const doCheckIn = async (projectId) => {
    if (shift && shift.project_id === projectId) { setPickOpen(false); toast.info("Na téhle stavbě už jsi přihlášen"); return; }
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const gps = await locate();
      await api.post("/attendance/check-in", { project_id: projectId, ...gps });
      try { storage.set(`pp-site-${user?.id}`, projectId); } catch { /* noop */ }
      if (isPhone()) {
        const pname = (ov?.my_projects || []).find((p) => p.id === projectId)?.name;
        const change = { label: "Změnit", onClick: () => setPickOpen(true) };
        gps.has_gps ? toast.success("Jsi v práci", { description: [pname, `od ${hmNow()}`].filter(Boolean).join(" · "), action: change })
          : toast.warning("Přihlášeno bez polohy", { ...PHONE_NO_GPS, action: change });
      } else gps.has_gps ? toast.success("Přihlášeno do práce.") : toast.warning("Přihlášeno bez GPS polohy.");
      setPickOpen(false); await load();
      window.dispatchEvent(new Event("pp-shift-changed"));
    } catch (e) { toast.error(attendanceError(e), { duration: e?.response?.status === 428 ? 15000 : 4000 }); }
    finally { setBusy(false); running.current = false; }
  };

  const startDay = async () => {
    // 201: bez stavby v týmu nejde výkaz odeslat a neodeslaný den by zablokoval i zítřek
    if (!(ov?.my_projects || []).length) {
      toast.error("Nejsi přiřazen k žádné aktivní stavbě.", { description: "Požádej vedoucího, ať tě přidá do týmu zakázky." });
      return;
    }
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const gps = await locate();
      await api.post("/daysheet/start", gps);
      if (isPhone()) gps.has_gps ? toast.success("Jsi v práci", { description: `Denní výkaz · od ${hmNow()}` }) : toast.warning("Přihlášeno bez polohy", PHONE_NO_GPS);
      else gps.has_gps ? toast.success("Přihlášeno do práce.") : toast.warning("Přihlášeno bez GPS polohy.");
      await load();
      window.dispatchEvent(new Event("pp-shift-changed"));
    } catch (e) { toast.error(attendanceError(e), { duration: e?.response?.status === 428 ? 15000 : 4000 }); }
    finally { setBusy(false); running.current = false; }
  };
  const endDay = async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const gps = await locate({ maxMs: 4000 });
      await api.post("/daysheet/end", { break_minutes: null, ...gps });
      await load();
      window.dispatchEvent(new Event("pp-shift-changed"));   // 201: lišta → „Vyplň výkaz“
      setCloseOpen(true);
    } catch (e) { toast.error(attendanceError(e), { duration: e?.response?.status === 428 ? 15000 : 4000 }); }
    finally { setBusy(false); running.current = false; }
  };

  const openCheckout = async () => {
    if (!shift) return;
    setReport(""); setDoneIds([]); setPhotos([]); setSiteTasks([]); setBreakMin("");
    setMaterials([]); setStockItems([]);
    setCheckoutOpen(true);
    try {
      const { data } = await api.get(`/tasks?project_id=${shift.project_id}`);
      const open = (data || []).filter((t) => !["Hotovo", "Schváleno"].includes(t.status));
      setSiteTasks(open);
    } catch { /* noop */ }
    // Subdodavatel materiál nehlásí — má ho ve své režii.
    if (user?.role !== "SUBCONTRACTOR") {
      try {
        const { data } = await api.get(`/projects/${shift.project_id}/stock-picker`);
        setStockItems(data || []);
      } catch { setStockItems([]); }
    }
  };

  const setMat = (i, k, v) => setMaterials((ms) => ms.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const removeMat = (i) => setMaterials((ms) => ms.filter((_, idx) => idx !== i));

  const toggleDone = (id) => setDoneIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) { toast.warning(`Max ${MAX_PHOTOS} fotek.`); return; }
    setUploading(true);
    try {
      // 216: komprese a nahrání až tří fotek naráz; nepovedená fotka nezahodí ostatní.
      const batch = files.slice(0, room);
      setUpProg({ done: 0, total: batch.length });
      const { urls, failed } = await uploadFiles(batch, { prepare: compressImage, onEach: (done, total) => setUpProg({ done, total }) });
      if (urls.length) setPhotos((p) => [...p, ...urls]);
      if (failed.length) toast.error(`Nenahrálo se ${failed.length} z ${batch.length}: ${apiError(failed[0].error)}`, { description: "Nahrané fotky zůstaly — chybějící přidej znovu." });
    } finally { setUploading(false); setUpProg(null); }
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const doCheckOut = async () => {
    if (!shift) return;
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      const gps = await locate({ maxMs: 4000 });
      await api.post("/attendance/check-out", {
        project_id: shift.project_id,
        note: report,
        break_minutes: breakMin === "" ? null : Number(breakMin),
        tasks_completed: doneIds,
        photos,
        materials: materials.filter((m) => m.stock_item_id && Number(m.qty) > 0)
          .map((m) => ({ stock_item_id: m.stock_item_id, qty: Number(m.qty) })),
        ...gps,
      });
      if (isPhone()) toast.success("Odhlášeno z práce", { description: [`zapsáno ${fmtHours(netHours)}`, shift.project_name].filter(Boolean).join(" · ") });
      else toast.success("Odhlášeno z práce.");
      setCheckoutOpen(false); setReport(""); setDoneIds([]); setPhotos([]); setBreakMin(""); setMaterials([]);
      await load();
      window.dispatchEvent(new Event("pp-shift-changed"));
    } catch (e) { toast.error(attendanceError(e), { duration: e?.response?.status === 428 ? 15000 : 4000 }); }
    finally { setBusy(false); running.current = false; }
  };

  // 219: graf hodin (vlastní /attendance/summary) startuje hned s kartou, ne až po
  // jejích datech — klíč ho drží na stejném místě stromu i po načtení (bez 2. requestu).
  const chart = <HoursChart key="hours-chart" />;
  if (!loaded || !ov) return headless ? null : (
    <div className="space-y-3">
      <div className="h-40 rounded-[16px] bg-secondary animate-pulse motion-reduce:animate-none" data-testid="my-shift-loading" />
      {chart}
    </div>
  );
  const overdue = ov.tasks_overdue || [];
  const today = ov.tasks_today || [];
  const todayTasks = [...overdue, ...today.filter((t) => !overdue.some((o) => o.id === t.id))];
  const taskModalIds = [...overdue.map((t) => t.id), ...today.filter((t) => !overdue.some((o) => o.id === t.id)).map((t) => t.id)];
  const odById = Object.fromEntries((pd?.tasks || []).map((t) => [t.id, t.overdueDays]));
  const lastShift = pd?.shift?.lastShift;
  const agenda = pd?.agenda || [];
  // 202: Gantt (/planovani) smí jen vedení a projektant — ostatním fáze neodkazují nikam
  // (dřív klepnutí jen problikl Přehled); skladník fáze všech staveb firmy neuvidí
  const canPlan = isManager(user) || user?.role === "DESIGNER";
  const agendaM = user?.role === "WAREHOUSE" ? agenda.filter((a) => !String(a.href || "").startsWith("/planovani")) : agenda;
  const pdHours = pd?.hours;
  const monthSub = pdHours ? `${pdHours.monthShiftCount} ${pdHours.monthShiftCount === 1 ? "směna" : pdHours.monthShiftCount < 5 ? "směny" : "směn"} · ${pdHours.monthProjectCount} ${pdHours.monthProjectCount === 1 ? "zakázka" : pdHours.monthProjectCount < 5 ? "zakázky" : "zakázek"}` : null;

  // Radix: další okno až po zavření mezikroku, jinak zůstane stránka zablokovaná
  const fromStep = (fn) => { setStepOpen(false); setTimeout(fn, 0); };
  const dialogs = (
    <>
      <ShiftStep open={stepOpen} shift={shift} onOpenChange={setStepOpen}
        onSwitch={() => fromStep(() => setPickOpen(true))} onLeave={() => fromStep(openCheckout)} />
      {/* Výběr stavby — hledání + nejbližší nahoře (199); počítač a tablet: panel zprava (236) */}
      <SitePicker open={pickOpen} onOpenChange={setPickOpen}
        title={shift ? "Přejít na jinou stavbu" : isPhone() ? "Kde dnes pracuješ?" : "Kam se přihlašuješ?"}
        subtitle={isPhone() ? null : shift ? "Klikem na stavbu se přesuneš." : "Klikem na stavbu zapíšeš příchod."}
        projects={ov.my_projects || []} selectedId={shift?.project_id || null} onPick={doCheckIn} busy={busy} />

      {/* Odhlášení (jen aktuální směna) — bohatý report */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent sheet data-testid="checkout-dialog" onOpenAutoFocus={(e) => e.preventDefault()}
          className="max-w-[660px] p-0 max-md:pb-0 gap-0 flex flex-col md:max-h-[92vh] overflow-hidden">
          <div className="shrink-0 pl-5 md:pl-6 pr-14 pt-1 md:pt-4 pb-4 border-b border-border flex max-sm:flex-col items-start justify-between gap-2 sm:gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-xl md:text-2xl font-black tracking-tight">Odhlásit se z práce</DialogTitle>
              <p className="text-sm text-muted-foreground truncate mt-0.5">
                {shift?.project_name} · dnes {formatTime(shift?.check_in)} → {formatTime(new Date().toISOString())}
              </p>
            </div>
            <span data-testid="checkout-elapsed"
              className="shrink-0 rounded-full px-3 py-1.5 text-[13px] font-bold whitespace-nowrap tabular-nums bg-success/15 text-success">
              {fmtHours(gross)} na stavbě
            </span>
          </div>

          <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-4 min-w-0">
            {siteTasks.length > 0 && (
              <Sec icon={ListChecks} title="Moje úkoly na stavbě">
                <div className="space-y-2" data-testid="checkout-tasks">
                  {siteTasks.map((t) => {
                    const on = doneIds.includes(t.id);
                    return (
                      <button key={t.id} type="button" onClick={() => toggleDone(t.id)} data-testid={`checkout-task-${t.id}`}
                        className={`w-full flex items-center gap-3 text-left rounded-[14px] border px-3 py-2.5 transition-colors ${on ? "border-success bg-success/10" : "border-border hover:bg-secondary"}`}>
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-success border-success text-white" : "border-muted-foreground/40"}`}>
                          {on && <Check size={14} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold block truncate">{t.title}</span>
                          <span className="text-xs text-muted-foreground">{on ? "označeno jako hotové" : "označit jako hotové"}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Sec>
            )}

            <Sec icon={Pencil} title={<>Na čem jsi dělal? <span className="font-normal normal-case tracking-normal text-muted-foreground">volitelné</span></>}>
              <Textarea data-testid="checkout-report" value={report} onChange={(e) => setReport(e.target.value)}
                placeholder="Např. betonáž základů, montáž bednění…" rows={3} className="w-full min-w-0" />
              <div className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground" data-testid="checkout-photos-label">
                Fotky {ov?.photos_required ? <span className="text-destructive normal-case">· povinné</span> : <span className="font-normal normal-case">· nepovinné</span>}
              </div>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="checkout-photos">
                  {photos.map((url, i) => (
                    <div key={i} className="relative w-[86px] h-[76px] rounded-[12px] overflow-hidden border border-border" data-testid={`checkout-photo-${i}`}>
                      <button type="button" onClick={() => setLightbox(url)} className="w-full h-full" aria-label="Zvětšit fotku">
                        <SignedImg url={url} alt="" thumb={86} className="w-full h-full object-cover" />
                      </button>
                      <button type="button" onClick={() => removePhoto(i)} aria-label="Smazat fotku"
                        className="absolute top-1 right-1 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center shadow"><X size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
              {photos.length < MAX_PHOTOS && (
                <PhotoUploadButton onClick={photoPick.open} busy={uploading} progress={upProg} testid="checkout-add-photo"
                  hint={photos.length ? `${photos.length} z ${MAX_PHOTOS}` : ov?.photos_required ? "Aspoň jedna fotka je povinná" : null} />
              )}
              <p className="text-[12px] text-muted-foreground">{ov?.photos_required ? "Vaše firma vyžaduje aspoň jednu fotku z práce." : `Fotky se odešlou stavbyvedoucímu ke schválení do deníku (max ${MAX_PHOTOS}).`}</p>
              {photoPick.inputs}
            </Sec>

            {user?.role !== "SUBCONTRACTOR" && user?.report_materials !== false && stockItems.length > 0 && (
              <Sec icon={Package} title={<>Spotřebovaný materiál <span className="font-normal normal-case tracking-normal text-muted-foreground">volitelné</span></>}
                action={<Button type="button" variant="outline" size="sm" onClick={() => setMaterials((ms) => [...ms, { stock_item_id: "", qty: "" }])} data-testid="material-add" className="h-9"><Package size={14} /> Přidat materiál</Button>}>
                <div className="space-y-2" data-testid="checkout-materials">
                  {materials.map((m, i) => {
                    const it = stockItems.find((s) => s.id === m.stock_item_id);
                    return (
                      <div key={i} className="grid grid-cols-[1fr_120px_40px] gap-2.5 items-center" data-testid={`material-row-${i}`}>
                        <Select value={m.stock_item_id} onValueChange={(v) => setMat(i, "stock_item_id", v)}>
                          <SelectTrigger className="h-12 min-w-0" data-testid={`material-item-${i}`}><SelectValue placeholder="Vyberte položku" /></SelectTrigger>
                          <SelectContent>
                            {stockItems.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} (skladem {s.qty} {s.unit})</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="relative min-w-0">
                          <Input type="number" min={0} value={m.qty} onChange={(e) => setMat(i, "qty", e.target.value)}
                            placeholder="0" className="h-12 w-full min-w-0 pr-12 tabular-nums" data-testid={`material-qty-${i}`} />
                          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground pointer-events-none">{it?.unit || ""}</span>
                        </div>
                        <button type="button" onClick={() => removeMat(i)} data-testid={`material-remove-${i}`}
                          className="h-10 w-10 grid place-items-center rounded-[12px] border border-border text-muted-foreground hover:border-destructive hover:text-destructive"><X size={16} /></button>
                      </div>
                    );
                  })}
                  <p className="text-[12px] text-muted-foreground">Hlášení jde stavbyvedoucímu ke schválení — ze skladu se odepíše až po jeho potvrzení.</p>
                </div>
              </Sec>
            )}

            <BreakChips value={breakMin} onChange={setBreakMin} informal testid="checkout-break" />
          </div>

          <div className="shrink-0 px-5 md:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-border flex flex-col md:flex-row md:items-center md:justify-end gap-2">
            {(netHours <= 0 || (ov?.photos_required && photos.length < 1)) && (
              <p className="text-[13px] text-destructive font-semibold text-center md:text-left md:mr-auto" data-testid="checkout-net">
                {ov?.photos_required && photos.length < 1 ? "Přidejte aspoň jednu fotku" : netText}
              </p>
            )}
            <Button onClick={doCheckOut} disabled={busy || uploading || (ov?.photos_required && photos.length < 1)} data-testid="checkout-confirm"
              className="h-[52px] md:h-12 w-full md:w-auto rounded-full font-extrabold text-[15px] px-8"><LogOut size={17} /> Potvrdit a ukončit směnu</Button>
          </div>
        </DialogContent>
      </Dialog>

      <TaskDetailModal taskId={taskModalId} taskIds={taskModalIds} open={!!taskModalId}
        onOpenChange={(o) => { if (!o) setTaskModalId(null); }} onNavigate={setTaskModalId} onChanged={load} />

      {isDaysheet && day && (
        <DayCloseModal open={closeOpen} day={day} projects={ov.my_projects || []} breakRule={breakRule} photosRequired={dayPhotosReq}
          onOpenChange={setCloseOpen} onSaved={() => { setCloseOpen(false); load(); window.dispatchEvent(new Event("pp-shift-changed")); }} />
      )}
      <PhotoLightbox url={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
  if (headless) return dialogs;

  return (
    <div className="space-y-3" data-testid="my-shift-block">
      {/* Moje směna */}
      {!hideCard && (
      <div className={`rounded-[16px] border border-border bg-card p-5 md:px-6 shadow-[0_1px_2px_rgba(0,0,0,.12)] ${runningStart ? "border-l-4 border-l-success" : ""}`} data-testid="my-shift-card">
        <div className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground mb-3">Moje směna</div>
        {runningStart ? (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-5 md:gap-7">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span className="relative flex w-2.5 h-2.5 shrink-0">
                  <span className="pp-ping absolute inline-flex w-full h-full rounded-full bg-success/60" />
                  <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-success" />
                </span>
                <span className="text-[17px] md:text-xl font-bold" data-testid="shift-project">
                  {isDaysheet ? "V práci" : <>V práci — {shift.project_name}</>}
                </span>
              </div>
              <div className="mt-1.5 flex items-baseline gap-2">
                <span className="text-[32px] md:text-[34px] leading-none font-extrabold tabular-nums text-success" data-testid="shift-elapsed">{hmm(runningStart)}</span>
                <span className="text-sm text-muted-foreground tabular-nums">h od {formatTime(runningStart)}</span>
              </div>
            </div>
            <div className="w-full md:w-auto">
              <Button onClick={isDaysheet ? endDay : openCheckout} disabled={busy} data-testid="checkout-btn"
                className="w-full md:w-auto h-[72px] md:h-16 px-8 rounded-[14px] bg-card hover:bg-brand/10 border-[1.5px] border-brand text-brand flex flex-col items-center justify-center gap-0.5">
                <span className="text-lg font-bold">Odhlásit se z práce</span>
                <span className="text-[11px] font-bold uppercase tracking-[.12em] opacity-80">
                  Ukončí směnu v {new Intl.DateTimeFormat("cs-CZ", { hour: "numeric", minute: "2-digit", timeZone: "Europe/Prague" }).format(new Date())}
                </span>
              </Button>
              {isDaysheet
                ? <p className="text-[13px] text-muted-foreground mt-2 text-center md:text-right">Zakázky se zapisují do výkazu po odhlášení</p>
                : <button onClick={() => setPickOpen(true)} className="block w-full md:w-auto md:ml-auto text-[13px] font-bold text-muted-foreground hover:text-foreground mt-2 text-center md:text-right" data-testid="switch-project-link">Přepnout zakázku</button>}
            </div>
          </div>
        ) : dayDraft ? (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-5 md:gap-7">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-brand shrink-0" />
                <span className="text-[17px] md:text-xl font-bold">{day.status === "returned" ? "Výkaz vrácen — uprav a odešli znovu" : "Směna ukončena — vyplň výkaz"}</span>
              </div>
              {day.returned_reason && <p className="text-[13px] text-destructive mt-1.5">{day.returned_reason}</p>}
            </div>
            <Button onClick={() => setCloseOpen(true)} data-testid="open-daysheet-btn"
              className="w-full md:w-auto h-[72px] md:h-16 px-8 rounded-[14px] bg-brand hover:bg-brand/90 text-white text-lg font-bold">Otevřít výkaz</Button>
          </div>
        ) : daySubmitted ? (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-5 md:gap-7">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="w-2.5 h-2.5 rounded-full bg-success shrink-0" />
                <span className="text-[17px] md:text-xl font-bold">Výkaz odeslán · {fmtHours(day.tracked_hours ?? (day.allocations || []).reduce((s, a) => s + (a.hours || 0), 0))}</span>
                <span className="inline-flex items-center h-6 px-2 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[11px] font-bold whitespace-nowrap" data-testid="daysheet-submitted-badge">čeká na schválení</span>
              </div>
              {(day.allocations || []).length > 0 && (
                <p className="text-[13px] text-muted-foreground mt-1.5 tabular-nums break-words">
                  {(day.allocations || []).map((a) => {
                    const nm = (ov.my_projects || []).find((p) => p.id === a.project_id)?.name || "—";
                    const h = a.hours || 0;
                    return `${nm} ${Math.floor(h)}:${String(Math.round((h % 1) * 60)).padStart(2, "0")}`;
                  }).join(" · ")}
                </p>
              )}
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border flex-wrap min-w-0" data-testid="day-summary-strip">
                <span className="w-9 h-9 rounded-full bg-success grid place-items-center shrink-0"><Check size={18} className="text-white" /></span>
                <span className="min-w-0">
                  <span className="block text-[15px] font-bold">{CLOSE_MSGS[(day.date.charCodeAt(day.date.length - 1) + (day.allocations || []).length) % CLOSE_MSGS.length]}</span>
                  <span className="block text-[12px] text-muted-foreground tabular-nums">
                    {formatTime(day.started_at)} – {day.ended_at ? formatTime(day.ended_at) : "—"}
                    {day.break_minutes > 0 ? ` · přestávka ${day.break_minutes} min odečtena` : ""}
                    {todayHours > (day.tracked_hours || 0) + 0.05 ? ` · dnes celkem ${fmtHours(todayHours)}` : ""}
                  </span>
                </span>
              </div>
            </div>
            <div className="w-full md:w-auto md:text-right">
              <Button onClick={startDay} disabled={busy} data-testid="checkin-again-btn"
                className="w-full md:w-auto h-[60px] md:h-14 px-7 rounded-[14px] bg-success hover:bg-success/90 text-white flex flex-col items-center justify-center gap-0.5">
                <span className="text-base font-bold flex items-center gap-2"><LogIn size={18} /> Přihlásit se do práce</span>
                <span className="text-[10px] font-bold uppercase tracking-[.12em] opacity-75">Další směna dnes</span>
              </Button>
              {canEditSubmitted ? (
                <>
                  <Button variant="outline" onClick={() => setCloseOpen(true)} data-testid="edit-daysheet-btn"
                    className="w-full md:w-auto h-11 mt-2 rounded-[12px] font-bold">Upravit výkaz</Button>
                  <p className="text-[12px] text-muted-foreground mt-2 text-center md:text-right">Ještě chvíli můžeš hodiny opravit, než je vedoucí uvidí.</p>
                </>
              ) : (
                <p className="text-[13px] text-muted-foreground mt-2 text-center md:text-right md:max-w-[260px] md:ml-auto">Výkaz čeká na schválení vedoucím.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-5 md:gap-7">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span className="relative flex w-2.5 h-2.5 shrink-0">
                  <span className="pp-ping absolute inline-flex w-full h-full rounded-full bg-destructive/60" />
                  <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-destructive" />
                </span>
                <span className="text-[17px] md:text-xl font-bold">Nejsi přihlášen do práce</span>
              </div>
              {lastShift && (
                <div className="text-[13px] text-muted-foreground mt-2 flex items-center gap-1.5 flex-wrap" data-testid="last-shift-line">
                  <History size={13} className="shrink-0" />
                  <span>Poslední směna: {formatDate(lastShift.date)} · {lastShift.projectName || "—"} · <span className="tabular-nums">{lastShift.from}–{lastShift.to}</span> · <span className="tabular-nums">{fmtHours(lastShift.hours)}</span></span>
                </div>
              )}
            </div>
            <Button onClick={isDaysheet ? startDay : () => setPickOpen(true)} disabled={busy} data-testid="checkin-btn"
              className="w-full md:w-auto h-[72px] md:h-16 px-8 rounded-[14px] bg-success hover:bg-success/90 text-white flex flex-col items-center justify-center gap-0.5">
              <span className="text-lg font-bold flex items-center gap-2"><LogIn size={20} /> Přihlásit se do práce</span>
              <span className="text-[10px] font-bold uppercase tracking-[.12em] opacity-75">{isDaysheet ? "Zakázky zapíšete po odhlášení" : "Zakázku vyberete v dalším kroku"}</span>
            </Button>
          </div>
        )}
      </div>
      )}

      {/* Odpracované hodiny — přesunuto pod úkoly (viz níže) */}

      {/* Dnešek — mobil: úkoly jdou odbavit tahem prstem (zadání 98) + události */}
      <div className="md:hidden bg-card border border-border rounded-[14px] p-4" data-testid="today-card-mobile">
        <h2 className="text-base font-bold tracking-tight mb-1">Dnešek</h2>
        <p className="text-[12px] text-muted-foreground mb-3" data-testid="today-swipe-hint">Tahni doprava → hotovo · doleva → akce · přidrž → smazat</p>
        <div className="space-y-2">
          {todayTasks.map((t) => (
            <SwipeRow key={t.id} t={t} isDelegated={() => false}
              onOpen={rowActions.openTask} onComplete={rowActions.completeRow}
              onReopen={rowActions.reopenRow} canReopen={false}
              {...rowActions.swipeProps}
              canEdit={false}
              canIdentity={rowActions.swipeProps.canIdentity(t)}
              canDelete={rowActions.swipeProps.canDelete(t)} />
          ))}
          {agendaM.map((a, i) => {
            const go = a.href && (canPlan || !String(a.href).startsWith("/planovani"));
            const Tag = go ? "button" : "div";
            return (
              <Tag key={`a-${i}`} {...(go ? { type: "button", onClick: () => nav(a.href) } : {})}
                className="block w-full text-left rounded-[12px] bg-secondary border border-border px-3.5 py-3">
                <span className="block text-[12px] font-bold text-brand tabular-nums">{a.time || "dnes"}</span>
                <span className="block text-sm font-semibold truncate">{a.title}</span>
                {a.subtitle && <span className="block text-xs text-muted-foreground truncate">{a.subtitle}</span>}
              </Tag>
            );
          })}
          {todayTasks.length + agendaM.length === 0 && (
            <p className="text-sm text-muted-foreground">Dnes nemáš nic naplánovaného.</p>
          )}
        </div>
      </div>
      {rowActions.sheets}

      {/* Moje úkoly na dnešek */}
      <div className="hidden md:block">
        <div className="bg-card border border-border rounded-[14px] p-4 md:p-5 min-w-0" data-testid="tasks-today-card">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="text-[17px] font-bold tracking-tight">Moje úkoly na dnešek</h2>
            <span className="rounded-full bg-secondary text-muted-foreground px-2.5 py-0.5 text-xs font-semibold tabular-nums" data-testid="tasks-today-count">
              {overdue.length + today.length} otevřené
            </span>
          </div>
          <div className="space-y-2">
            {[...overdue.map((t) => ({ ...t, _over: true })), ...today.filter((t) => !overdue.some((o) => o.id === t.id))].map((t) => {
              const od = t._over ? (odById[t.id] || 0) : 0;
              return (
                <button key={t.id} onClick={() => setTaskModalId(t.id)} data-testid={t._over ? `overdue-task-${t.id}` : `today-task-${t.id}`}
                  className={`w-full grid grid-cols-[20px_1fr_auto] items-center gap-3 text-left rounded-[12px] border px-3.5 py-3 transition-colors ${t._over ? "bg-destructive/5 border-destructive/30 hover:border-destructive/50" : "border-border hover:bg-secondary/50"}`}>
                  <span className={`w-[18px] h-[18px] rounded-[6px] border-2 ${t._over ? "border-destructive" : "border-muted-foreground/40"}`} />
                  <span className="min-w-0">
                    <span className="font-semibold block truncate text-sm">{t.title}</span>
                    {t.project_name && <span className="text-xs text-muted-foreground truncate block">{t.project_name}</span>}
                  </span>
                  <span className="text-right shrink-0">
                    {t._over ? (
                      <>
                        <span className="block text-xs font-bold text-destructive whitespace-nowrap">{od > 0 ? `${od} ${od === 1 ? "den" : od < 5 ? "dny" : "dní"} po termínu` : "po termínu"}</span>
                        {t.deadline && <span className="block text-xs text-muted-foreground tabular-nums">{formatDate(t.deadline)}</span>}
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">dnes</span>
                    )}
                  </span>
                </button>
              );
            })}
            {overdue.length + today.length === 0 && (
              <p className="text-sm text-muted-foreground flex items-center gap-2"><CalendarClock size={15} /> Nemáš žádné otevřené úkoly.</p>
            )}
          </div>
        </div>
      </div>

      {/* Odpracované hodiny — pod úkoly (mobil i desktop) */}
      {chart}

      {dialogs}
    </div>
  );
}
